import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  ArrowLeft,
  Building2,
  Settings,
  Users,
  BarChart3,
  Globe,
  Plus,
  Edit,
  Trash,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  Filter,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service Icon Component
const ServiceIcon = ({ icon, className = "text-2xl" }) => {
  return <span className={className}>{icon}</span>;
};

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    yellow: "bg-yellow-100 text-yellow-600",
    purple: "bg-purple-100 text-purple-600",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          <div className={`p-3 rounded-lg ${colors[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Service Card for Admin
const ServiceAdminCard = ({ serviceKey, service, enabled, onToggle, countryCode }) => {
  return (
    <Card className={`border ${enabled ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50/50'}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <ServiceIcon icon={service.icon || "📋"} />
            <div>
              <h4 className="font-medium">{service.name}</h4>
              <p className="text-sm text-gray-500 line-clamp-2">{service.description}</p>
              <div className="flex gap-2 mt-2">
                <Badge variant="outline" className="text-xs">
                  {service.service_type || 'service'}
                </Badge>
                {service.custom && (
                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                    Custom
                  </Badge>
                )}
                {service.country_only && (
                  <Badge className="bg-blue-100 text-blue-700 text-xs">
                    {service.country_only} Only
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(checked) => onToggle(serviceKey, checked)}
          />
        </div>
      </CardContent>
    </Card>
  );
};

// Subcategory Configuration Panel
const SubcategoryConfigPanel = ({ subcategory, country, disabledServices, customServices, onRefresh }) => {
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newService, setNewService] = useState({
    key: '',
    name: '',
    icon: '📋',
    description: '',
    service_type: 'service',
  });

  useEffect(() => {
    if (subcategory?.services) {
      // Merge base services with custom services
      const mergedServices = { ...subcategory.services };
      if (customServices?.[subcategory.slug]) {
        Object.entries(customServices[subcategory.slug]).forEach(([key, service]) => {
          mergedServices[key] = service;
        });
      }
      setServices(mergedServices);
    }
  }, [subcategory, customServices]);

  const handleToggleService = async (serviceKey, enabled) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/admin/subcategory/${subcategory.slug}/toggle?service_key=${serviceKey}&country=${country}&enabled=${enabled}`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        onRefresh?.();
      } else {
        toast.error('Failed to update service');
      }
    } catch (error) {
      toast.error('Failed to toggle service');
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async () => {
    if (!newService.key || !newService.name) {
      toast.error('Service key and name are required');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/admin/subcategory/${subcategory.slug}/add-service?country=${country}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newService),
        }
      );
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setShowAddDialog(false);
        setNewService({
          key: '',
          name: '',
          icon: '📋',
          description: '',
          service_type: 'service',
        });
        onRefresh?.();
      } else {
        toast.error('Failed to add service');
      }
    } catch (error) {
      toast.error('Failed to add service');
    } finally {
      setLoading(false);
    }
  };

  const isServiceEnabled = (serviceKey) => {
    const disabled = disabledServices?.[subcategory.slug] || [];
    return !disabled.includes(serviceKey);
  };

  return (
    <div className="space-y-4">
      {/* Subcategory Header */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-3">
          <ServiceIcon icon={subcategory.subcategory_icon} className="text-3xl" />
          <div>
            <h3 className="text-lg font-semibold">{subcategory.subcategory_name}</h3>
            <p className="text-sm text-gray-500">
              {Object.keys(services).length} services configured
            </p>
          </div>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Custom Service
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Custom Service for {country}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Service Key (unique identifier)</Label>
                <Input
                  placeholder="e.g., custom_service_name"
                  value={newService.key}
                  onChange={(e) => setNewService({ ...newService, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                />
              </div>
              <div>
                <Label>Service Name</Label>
                <Input
                  placeholder="e.g., Custom Service"
                  value={newService.name}
                  onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Icon (emoji)</Label>
                <Input
                  placeholder="📋"
                  value={newService.icon}
                  onChange={(e) => setNewService({ ...newService, icon: e.target.value })}
                  maxLength={4}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  placeholder="Service description..."
                  value={newService.description}
                  onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                />
              </div>
              <div>
                <Label>Service Type</Label>
                <Select
                  value={newService.service_type}
                  onValueChange={(value) => setNewService({ ...newService, service_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="supply">Supply</SelectItem>
                    <SelectItem value="transaction">Transaction</SelectItem>
                    <SelectItem value="rental">Rental</SelectItem>
                    <SelectItem value="consultation">Consultation</SelectItem>
                    <SelectItem value="listing">Listing</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="financial">Financial</SelectItem>
                    <SelectItem value="legal">Legal</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleAddService} disabled={loading} className="w-full">
                {loading ? 'Adding...' : 'Add Service'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Compliance Info */}
      {subcategory.compliance_level && (
        <div className={`p-3 rounded-lg flex items-center gap-2 ${
          subcategory.compliance_level === 'high' ? 'bg-red-50 text-red-700' :
          subcategory.compliance_level === 'medium' ? 'bg-yellow-50 text-yellow-700' :
          'bg-green-50 text-green-700'
        }`}>
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">
            Compliance Level: <strong className="capitalize">{subcategory.compliance_level}</strong>
            {subcategory.requires_certification && ' • Certification Required'}
          </span>
        </div>
      )}

      {/* Disclaimer */}
      {subcategory.disclaimer && (
        <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
          <strong>Disclaimer:</strong> {subcategory.disclaimer}
        </div>
      )}

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(services).map(([key, service]) => (
          <ServiceAdminCard
            key={key}
            serviceKey={key}
            service={service}
            enabled={isServiceEnabled(key)}
            onToggle={handleToggleService}
            countryCode={country}
          />
        ))}
      </div>
    </div>
  );
};

// Provider Card
const ProviderCard = ({ provider, onVerify }) => {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {provider.logo_url ? (
            <img
              src={provider.logo_url}
              alt={provider.name}
              className="w-16 h-16 rounded-lg object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold">{provider.name}</h4>
              {provider.verified && (
                <Badge className="bg-green-100 text-green-700">Verified</Badge>
              )}
              <Badge variant="outline">{provider.country}</Badge>
            </div>
            {provider.business_name && (
              <p className="text-sm text-gray-500">{provider.business_name}</p>
            )}
            <p className="text-sm text-gray-600 mt-1">{provider.service_area}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-xs">
                {provider.subcategory_slug?.replace(/-/g, ' ')}
              </Badge>
              <span className="text-sm text-gray-500">
                ⭐ {provider.rating?.toFixed(1)} ({provider.review_count} reviews)
              </span>
            </div>
          </div>
          {provider.status === 'pending_verification' && (
            <div className="flex flex-col gap-2">
              <Button size="sm" onClick={() => onVerify(provider.id, true)}>
                <CheckCircle className="w-4 h-4 mr-1" />
                Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => onVerify(provider.id, false)}>
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Main Admin Component
export default function AdminBusinessServices() {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState('UK');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [providers, setProviders] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);

  // Fetch subcategories for selected country
  const fetchSubcategories = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/config/subcategories?country=${selectedCountry}`
      );
      const data = await response.json();
      if (data.success) {
        setSubcategories(data.subcategories || []);
      }
    } catch (error) {
      console.error('Failed to fetch subcategories:', error);
      toast.error('Failed to load subcategories');
    }
  }, [selectedCountry]);

  // Fetch providers
  const fetchProviders = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/providers?country=${selectedCountry}&limit=50`
      );
      const data = await response.json();
      if (data.success) {
        setProviders(data.providers || []);
      }
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    }
  }, [selectedCountry]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/admin/stats?country=${selectedCountry}`
      );
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [selectedCountry]);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/admin/settings/${selectedCountry}`
      );
      const data = await response.json();
      if (data.success) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, [selectedCountry]);

  // Load all data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSubcategories(),
        fetchProviders(),
        fetchStats(),
        fetchSettings(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [selectedCountry, fetchSubcategories, fetchProviders, fetchStats, fetchSettings]);

  // Refresh data
  const handleRefresh = () => {
    fetchSubcategories();
    fetchProviders();
    fetchStats();
    fetchSettings();
  };

  // Seed demo providers
  const handleSeedDemo = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/seed-demo-providers`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        handleRefresh();
      }
    } catch (error) {
      toast.error('Failed to seed demo data');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Building2 className="w-6 h-6" />
                  Business Services Admin
                </h1>
                <p className="text-sm text-gray-500">
                  Manage business services by country
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="w-40">
                  <Globe className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                  <SelectItem value="LK">🇱🇰 Sri Lanka</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={handleRefresh}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={handleSeedDemo}>
                Seed Demo Data
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="services">
              <Settings className="w-4 h-4 mr-2" />
              Services Config
            </TabsTrigger>
            <TabsTrigger value="providers">
              <Users className="w-4 h-4 mr-2" />
              Providers
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-24 bg-gray-200 animate-pulse rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <StatsCard
                    title="Total Providers"
                    value={stats?.providers?.total || 0}
                    icon={Users}
                    color="blue"
                  />
                  <StatsCard
                    title="Active Providers"
                    value={stats?.providers?.active || 0}
                    icon={CheckCircle}
                    color="green"
                  />
                  <StatsCard
                    title="Pending Verification"
                    value={stats?.providers?.pending_verification || 0}
                    icon={AlertTriangle}
                    color="yellow"
                  />
                  <StatsCard
                    title="Total Inquiries"
                    value={stats?.inquiries?.total || 0}
                    icon={Building2}
                    color="purple"
                  />
                </div>

                {/* Subcategory Overview */}
                <Card>
                  <CardHeader>
                    <CardTitle>Services by Subcategory</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {subcategories.map((subcat) => (
                        <div
                          key={subcat.slug}
                          className="p-4 border rounded-lg hover:border-blue-300 cursor-pointer transition"
                          onClick={() => {
                            setSelectedSubcategory(subcat);
                            setActiveTab('services');
                          }}
                        >
                          <ServiceIcon icon={subcat.subcategory_icon} className="text-2xl mb-2" />
                          <h4 className="font-medium text-sm">{subcat.subcategory_name}</h4>
                          <p className="text-xs text-gray-500">
                            {Object.keys(subcat.services || {}).length} services
                          </p>
                          {stats?.by_subcategory?.[subcat.slug] && (
                            <Badge className="mt-2 text-xs">
                              {stats.by_subcategory[subcat.slug]} providers
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Country Info */}
                <Card>
                  <CardHeader>
                    <CardTitle>Country Configuration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-500">Country</Label>
                        <p className="font-medium">{settings?.name || selectedCountry}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Currency</Label>
                        <p className="font-medium">{settings?.currency_symbol} ({settings?.currency})</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Enabled Subcategories</Label>
                        <p className="font-medium">{settings?.enabled_subcategories?.length || 0}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Custom Settings</Label>
                        <Badge variant={settings?.is_default ? "outline" : "default"}>
                          {settings?.is_default ? 'Using Defaults' : 'Customized'}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Services Config Tab */}
          <TabsContent value="services">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Subcategory List */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-gray-500 px-2">SUBCATEGORIES</h3>
                {subcategories.map((subcat) => (
                  <button
                    key={subcat.slug}
                    onClick={() => setSelectedSubcategory(subcat)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition ${
                      selectedSubcategory?.slug === subcat.slug
                        ? 'bg-blue-100 border-blue-300 border'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <ServiceIcon icon={subcat.subcategory_icon} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{subcat.subcategory_name}</p>
                      <p className="text-xs text-gray-500">
                        {Object.keys(subcat.services || {}).length} services
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Service Configuration */}
              <div className="md:col-span-3">
                {selectedSubcategory ? (
                  <SubcategoryConfigPanel
                    subcategory={selectedSubcategory}
                    country={selectedCountry}
                    disabledServices={settings?.disabled_services}
                    customServices={settings?.custom_services}
                    onRefresh={handleRefresh}
                  />
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-400">
                    <div className="text-center">
                      <Settings className="w-12 h-12 mx-auto mb-2" />
                      <p>Select a subcategory to configure services</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <div className="space-y-4">
              {/* Filter Bar */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Search providers..." className="pl-10" />
                </div>
                <Select defaultValue="all">
                  <SelectTrigger className="w-40">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Providers List */}
              {providers.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <h3 className="font-medium text-gray-500">No providers found</h3>
                    <p className="text-sm text-gray-400 mb-4">
                      Click "Seed Demo Data" to add sample providers
                    </p>
                    <Button onClick={handleSeedDemo}>
                      Seed Demo Providers
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {providers.map((provider) => (
                    <ProviderCard
                      key={provider.id}
                      provider={provider}
                      onVerify={async (id, approved) => {
                        toast.success(`Provider ${approved ? 'approved' : 'rejected'}`);
                        handleRefresh();
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
